import { useState, useMemo, useRef } from 'react';

// Sell-price-over-time chart for a single catalogue product. Each point is one
// SOW hardware line's actual unit sell price on its SOW date, so you can see how
// the price this product has been sold at has moved over time — something the
// catalogue's single static sell_price can't show. Self-contained SVG, no deps.

const VB_W = 720, VB_H = 250;
const PAD = { l: 52, r: 14, t: 14, b: 34 };
const PLOT_W = VB_W - PAD.l - PAD.r;
const PLOT_H = VB_H - PAD.t - PAD.b;
const DAY = 86400000;

function parseTs(s) {
  if (!s) return null;
  let t = s.includes('T') ? s : s.replace(' ', 'T');
  if (!/[Z+]/.test(t)) t += 'Z';           // D1 stores UTC without a zone marker
  const d = new Date(t);
  return isNaN(d.getTime()) ? null : d.getTime();
}

function fmtTick(ts, spanDays) {
  const d = new Date(ts);
  if (spanDays <= 92) return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

export default function SellPriceChart({ usage, catalogSell, fmt }) {
  const [hover, setHover] = useState(null); // index of hovered point
  const svgRef = useRef(null);

  const pts = useMemo(() => (usage || [])
    .map(u => ({ t: parseTs(u.sale_date), p: Number(u.unit_sell), ref: u.sow_reference, desc: u.item_description, qty: u.quantity }))
    .filter(pt => pt.t != null && isFinite(pt.p))
    .sort((a, b) => a.t - b.t), [usage]);

  const model = useMemo(() => {
    if (!pts.length) return null;
    const start = pts[0].t;
    const end = pts[pts.length - 1].t;
    const spanDays = Math.max(1, (end - start) / DAY);
    const allP = pts.map(pt => pt.p);
    if (catalogSell != null && isFinite(Number(catalogSell))) allP.push(Number(catalogSell));
    let minP = Math.min(...allP), maxP = Math.max(...allP);
    if (minP === maxP) { const b = Math.max(1, Math.abs(minP) * 0.1); minP -= b; maxP += b; }
    else { const b = (maxP - minP) * 0.14; minP -= b; maxP += b; }
    if (minP < 0) minP = 0;
    return { start, end, spanDays, minP, maxP };
  }, [pts, catalogSell]);

  if (!pts.length || !model) return null;

  const singleDay = model.end === model.start;
  const x = (t) => singleDay ? PAD.l + PLOT_W / 2 : PAD.l + ((t - model.start) / (model.end - model.start)) * PLOT_W;
  const y = (p) => PAD.t + (1 - (p - model.minP) / (model.maxP - model.minP || 1)) * PLOT_H;

  const linePath = pts.length > 1 && !singleDay
    ? pts.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${x(pt.t).toFixed(1)} ${y(pt.p).toFixed(1)}`).join(' ')
    : '';

  const yTicks = 4;
  const gridVals = Array.from({ length: yTicks + 1 }, (_, i) => model.minP + (i / yTicks) * (model.maxP - model.minP));
  const xTickN = model.spanDays <= 92 ? 4 : 5;
  const xTicks = singleDay ? [model.start] : Array.from({ length: xTickN + 1 }, (_, i) => model.start + (i / xTickN) * (model.end - model.start));

  const sells = pts.map(pt => pt.p);
  const minSell = Math.min(...sells), maxSell = Math.max(...sells);
  const avgSell = sells.reduce((s, v) => s + v, 0) / sells.length;
  const catNum = catalogSell != null && isFinite(Number(catalogSell)) ? Number(catalogSell) : null;

  return (
    <div className="mb-1">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-[8px] font-bold uppercase text-gray-400">Sell price over time</div>
        <div className="flex items-center gap-3 text-[8px] text-gray-500">
          <span>Min <span className="font-semibold text-gray-700">{fmt(minSell)}</span></span>
          <span>Avg <span className="font-semibold text-gray-700">{fmt(Math.round(avgSell * 100) / 100)}</span></span>
          <span>Max <span className="font-semibold text-gray-700">{fmt(maxSell)}</span></span>
          {catNum != null && <span className="inline-flex items-center gap-1"><span className="w-3 border-t border-dashed border-blue-500" /> Catalogue {fmt(catNum)}</span>}
        </div>
      </div>

      <div className="border border-gray-100 rounded-lg bg-white p-1">
        <svg ref={svgRef} viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full" style={{ height: 'auto' }}
          onMouseLeave={() => setHover(null)}>
          {gridVals.map((v, i) => (
            <g key={i}>
              <line x1={PAD.l} y1={y(v)} x2={VB_W - PAD.r} y2={y(v)} stroke="#eef0f3" strokeWidth="1" />
              <text x={PAD.l - 6} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#9ca3af">{fmt(v)}</text>
            </g>
          ))}
          {xTicks.map((t, i) => (
            <text key={i} x={x(t)} y={VB_H - PAD.b + 16} textAnchor="middle" fontSize="9" fill="#9ca3af">
              {fmtTick(t, model.spanDays)}
            </text>
          ))}
          {/* Catalogue sell-price reference line */}
          {catNum != null && (
            <line x1={PAD.l} y1={y(catNum)} x2={VB_W - PAD.r} y2={y(catNum)} stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="4 3" />
          )}
          {/* Sell-price trend line */}
          {linePath && <path d={linePath} fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinejoin="round" />}
          {/* Points — larger hit area for hover */}
          {pts.map((pt, i) => (
            <g key={i}>
              <circle cx={x(pt.t)} cy={y(pt.p)} r="10" fill="transparent"
                onMouseEnter={() => setHover(i)} style={{ cursor: 'pointer' }} />
              <circle cx={x(pt.t)} cy={y(pt.p)} r={hover === i ? 4 : 2.8} fill={hover === i ? '#fff' : '#7c3aed'}
                stroke="#7c3aed" strokeWidth="2" style={{ pointerEvents: 'none' }} />
            </g>
          ))}
          {hover != null && (
            <line x1={x(pts[hover].t)} y1={PAD.t} x2={x(pts[hover].t)} y2={VB_H - PAD.b} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" style={{ pointerEvents: 'none' }} />
          )}
        </svg>

        {hover != null && (
          <div className="text-[9px] text-gray-600 px-2 pb-1 flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-400">{new Date(pts[hover].t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</span>
            <span className="font-mono font-semibold text-navy">{pts[hover].ref}</span>
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ background: '#7c3aed' }} />
              <span className="font-semibold text-gray-700">{fmt(pts[hover].p)}</span>
            </span>
            {pts[hover].desc && <span className="text-gray-400 truncate max-w-[280px]">{pts[hover].desc}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
